import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Switch, 
  message,
  Popconfirm,
  Card,
  Statistic,
  Row,
  Col,
  Alert
} from 'antd';
import type { TooltipProps } from 'antd';
import { Tooltip } from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  PlusOutlined,
  UserOutlined,
  TeamOutlined
} from '@ant-design/icons';
import request from '../../../utils/request';

const { Option } = Select;
const { TextArea } = Input;

interface RoleTemplate {
  id: number;
  name: string;
  description: string;
  role: string;
  sub_role?: string;
  permissions: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface PermissionResource {
  id: number;
  key: string;
  name: string;
  category?: string;
  description?: string;
  is_active: boolean;
}

interface PermissionAction {
  id: number;
  key: string;
  name: string;
  description?: string;
  is_active: boolean;
}

interface ResourceActionMap {
  id: number;
  resource: number;
  resource_key: string;
  action: number;
  action_key: string;
  allowed_scopes: string[];
  is_enabled: boolean;
}

interface TemplatePermissionRow {
  id?: number;
  resource: number;
  resource_key: string;
  action: number;
  action_key: string;
  allowed_scopes: string[];
  is_enabled: boolean;
  is_allowed: boolean;
  scope?: string;
}

const RoleManagement: React.FC = () => {
  const [templates, setTemplates] = useState<RoleTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<RoleTemplate | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [form] = Form.useForm();

  // 字典数据与模板权限行
  const [resList, setResList] = useState<PermissionResource[]>([]);
  const [actList, setActList] = useState<PermissionAction[]>([]);
  const [resActList, setResActList] = useState<ResourceActionMap[]>([]);
  const [permRows, setPermRows] = useState<TemplatePermissionRow[]>([]);
  // 角色模板权限表（模态内）受控分页
  const [permPagination, setPermPagination] = useState<{ current: number; pageSize: number }>({ current: 1, pageSize: 20 });
  // 按资源名称筛选（基于资源 key 映射）
  const [permResourceFilter, setPermResourceFilter] = useState<string | undefined>(undefined);

  const base = '/api/v1/permission-management';

  // 获取分页接口全量数据（兼容 DRF 分页 与 非分页返回）
  const fetchAllPages = async (path: string): Promise<any[]> => {
    let url: string | null = `${base}${path}`;
    const items: any[] = [];
    let guard = 0;
    while (url && guard < 100) {
      const resp = await request.get(url);
      const data = resp.data;
      if (Array.isArray(data)) {
        items.push(...data);
        url = null;
      } else {
        if (Array.isArray(data.results)) items.push(...data.results);
        url = data.next || null;
      }
      guard += 1;
    }
    return items;
  };

  const ROLE_CHOICES = [
    { value: 'admin', label: '管理员' },
    { value: 'hq_staff', label: '总部员工' },
    { value: 'warehouse_supervisor', label: '仓库主管' },
    { value: 'warehouse_staff', label: '仓库员工' },
    { value: 'customer', label: '客户' },
  ];

  const getSubRoleChoices = (role: string) => {
    if (role === 'customer') {
      return [
        { value: 'main_account', label: '主账号' },
        { value: 'sub_account', label: '子账号' },
      ];
    }
    return [];
  };

  useEffect(() => {
    fetchDictionaries();
    fetchTemplates();
  }, []);

  const fetchDictionaries = async () => {
    try {
      const [allRes, allAct, allMap] = await Promise.all([
        fetchAllPages('/resources/'),
        fetchAllPages('/actions/'),
        fetchAllPages('/resource-actions/'),
      ]);
      setResList(allRes);
      setActList(allAct);
      const maps: ResourceActionMap[] = (allMap || []).map((m: any) => ({
        id: m.id,
        resource: m.resource,
        resource_key: m.resource_key || '',
        action: m.action,
        action_key: m.action_key || '',
        allowed_scopes: m.allowed_scopes || [],
        is_enabled: m.is_enabled,
      }));
      setResActList(maps);
    } catch (e) {
      message.error('加载权限字典失败');
    }
  };

  // 资源 key -> 中文名称 映射
  const resourceKeyToName = useMemo(() => {
    const map: Record<string, string> = {};
    (resList || []).forEach(r => { if (r?.key) map[r.key] = r.name || r.key; });
    return map;
  }, [resList]);

  // 按资源筛选后的权限行
  const filteredPermRows = useMemo(() => {
    if (!permResourceFilter) return permRows;
    return permRows.filter(r => r.resource_key === permResourceFilter);
  }, [permRows, permResourceFilter]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await request.get('/api/v1/permission-management/templates/');
      setTemplates(response.data.results || response.data);
    } catch (error) {
      message.error('获取角色模板失败');
      console.error('获取角色模板失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTemplate(null);
    setSelectedRole('');
    form.resetFields();
    // 初始化权限行（基于资源-动作映射）
    const rows: TemplatePermissionRow[] = resActList
      .filter((m) => m.is_enabled)
      .map((m) => ({
        resource: m.resource,
        resource_key: m.resource_key,
        action: m.action,
        action_key: m.action_key,
        allowed_scopes: m.allowed_scopes || [],
        is_enabled: m.is_enabled,
        is_allowed: false,
        scope: (m.allowed_scopes && m.allowed_scopes[0]) || 'global',
      }));
    setPermRows(rows);
    setPermPagination({ current: 1, pageSize: permPagination.pageSize });
    setModalVisible(true);
  };

  const handleEdit = (template: RoleTemplate) => {
    setEditingTemplate(template);
    const frontendData = convertFromBackendFormat(template);
    form.setFieldsValue(frontendData);
    setSelectedRole(frontendData.role || '');
    // 构建权限行并覆盖模板已有权限
    const baseRows: TemplatePermissionRow[] = resActList
      .filter((m) => m.is_enabled)
      .map((m) => ({
        resource: m.resource,
        resource_key: m.resource_key,
        action: m.action,
        action_key: m.action_key,
        allowed_scopes: m.allowed_scopes || [],
        is_enabled: m.is_enabled,
        is_allowed: false,
        scope: (m.allowed_scopes && m.allowed_scopes[0]) || 'global',
      }));

    try {
      const perms = (template as any).permissions || {};
      const patched = baseRows.map((row) => {
        const resPerm = perms[row.resource_key] || {};
        const actPerm = resPerm[row.action_key];
        if (actPerm !== undefined) {
          if (typeof actPerm === 'boolean') {
            return { ...row, is_allowed: !!actPerm };
          }
          if (typeof actPerm === 'object') {
            const scope = actPerm.scope && row.allowed_scopes.includes(actPerm.scope)
              ? actPerm.scope
              : row.scope;
            return { ...row, is_allowed: !!actPerm.is_allowed, scope };
          }
        }
        return row;
      });
      setPermRows(patched);
      setPermPagination({ current: 1, pageSize: permPagination.pageSize });
    } catch (e) {
      setPermRows(baseRows);
      setPermPagination({ current: 1, pageSize: permPagination.pageSize });
    }
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await request.delete(`/api/v1/permission-management/templates/${id}/`);
      message.success('删除成功');
      fetchTemplates();
    } catch (error) {
      message.error('删除失败');
      console.error('删除失败:', error);
    }
  };

  const handleApplyToMatrix = async (templateId: number) => {
    try {
      console.log('🔄 应用角色模板到权限矩阵:', templateId);
      const response = await request.post(`/api/v1/permission-management/templates/${templateId}/apply_to_matrix/`);
      
      if (response.data.status === 'success') {
        message.success(response.data.message);
        console.log('✅ 角色模板应用成功');
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      console.error('❌ 应用角色模板失败:', error);
      message.error('应用角色模板失败');
    }
  };

  const handleBatchApplyToMatrix = async (templateIds: number[]) => {
    try {
      console.log('🔄 批量应用角色模板到权限矩阵:', templateIds);
      const response = await request.post('/api/v1/permission-management/templates/batch_apply_to_matrix/', {
        template_ids: templateIds
      });
      
      if (response.data.status === 'success') {
        message.success(response.data.message);
        console.log('✅ 批量应用成功');
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      console.error('❌ 批量应用失败:', error);
      message.error('批量应用失败');
    }
  };

  const handleSyncAllToMatrix = async () => {
    try {
      console.log('🔄 同步所有激活的角色模板到权限矩阵');
      const response = await request.post('/api/v1/permission-management/templates/sync_all_to_matrix/');
      
      if (response.data.status === 'success') {
        message.success(response.data.message);
        console.log('✅ 同步完成');
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      console.error('❌ 同步失败:', error);
      message.error('同步失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // 转换前端数据格式为后端格式
      const backendData = convertToBackendFormat(values);
      backendData.permissions = buildPermissionsPayload();
      
      if (editingTemplate) {
        await request.put(`/api/v1/permission-management/templates/${editingTemplate.id}/`, backendData);
        message.success('更新成功');
      } else {
        await request.post('/api/v1/permission-management/templates/', backendData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchTemplates();
    } catch (error) {
      message.error(editingTemplate ? '更新失败' : '创建失败');
      console.error('提交失败:', error);
    }
  };

  const buildPermissionsPayload = () => {
    const payload: any = {};
    permRows.forEach((row) => {
      if (!payload[row.resource_key]) payload[row.resource_key] = {};
      // 始终提交显式结构，便于后端校验 scope
      payload[row.resource_key][row.action_key] = {
        is_allowed: !!row.is_allowed,
        scope: row.scope || 'global',
      };
    });
    return payload;
  };

  // 转换前端数据格式为后端格式
  const convertToBackendFormat = (formData: any) => {
    const { role, sub_role, ...otherData } = formData;
    // 非 customer：后端仅需 role
    if (role !== 'customer') {
      return { ...otherData, role };
    }
    // customer：可带 sub_role（main_account/sub_account）
    return { ...otherData, role: 'customer', sub_role: sub_role || null };
  };

  // 转换后端数据格式为前端格式
  const convertFromBackendFormat = (backendData: any) => {
    const { role, sub_role, ...otherData } = backendData;
    if (role === 'customer') {
      return { ...otherData, role: 'customer', sub_role };
    }
    return { ...otherData, role };
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: Record<string, string> = {
      admin: '管理员',
      hq_staff: '总部员工',
      warehouse_supervisor: '仓库主管',
      warehouse_staff: '仓库员工',
      customer: '客户',
    };
    return roleMap[role] || role;
  };

  const getSubRoleDisplayName = (subRole: string) => {
    const subRoleMap: Record<string, string> = {
      main_account: '主账号',
      sub_account: '子账号',
      hq_staff: '总部员工',
      warehouse_supervisor: '仓库主管',
      warehouse_staff: '仓库员工',
    };
    return subRoleMap[subRole] || subRole;
  };

  const columns = [
    {
      title: '模板名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: RoleTemplate) => {
        // 处理角色显示
        let roleDisplay = '';
        let subRoleDisplay = '';
        
        if (record.role === 'customer' && record.sub_role) {
          roleDisplay = '客户';
          subRoleDisplay = getSubRoleDisplayName(record.sub_role);
        } else {
          roleDisplay = getRoleDisplayName(record.role);
        }
        
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {roleDisplay}
              {subRoleDisplay && ` (${subRoleDisplay})`}
            </div>
          </div>
        );
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '激活' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record: RoleTemplate) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button 
            type="link" 
            onClick={() => handleApplyToMatrix(record.id)}
            style={{ color: '#1890ff' }}
          >
            应用到权限矩阵
          </Button>
          <Popconfirm
            title="确定要删除这个角色模板吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 统计数据
  const activeTemplates = templates.filter(t => t.is_active).length;
  const totalTemplates = templates.length;

  return (
    <div className="role-management">
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="总模板数"
              value={totalTemplates}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="激活模板数"
              value={activeTemplates}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="禁用模板数"
              value={totalTemplates - activeTemplates}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="角色管理 - 使用说明"
          description={
            <div>
              <div>1) 为某个角色（客户可选主/子账号）配置可进行的操作与数据范围。</div>
              <div>2) 在下方“权限配置”表格中勾选允许，并为每个资源-动作选择范围（来自“资源-动作映射”）。</div>
              <div>3) 保存模板后，点击“应用到权限矩阵”或“同步所有模板到矩阵”使配置立即生效。</div>
              <div>提示：范围可在“资源与动作 → 资源-动作映射”与“资源 → 范围配置（向导）”中预设和校验。</div>
              <div style={{ marginTop: 6 }}>
                <strong>范围规则</strong>：
                <div>• <strong>允许范围（映射）</strong>：设置可被授予的范围白名单与总开关（上限/护栏）。</div>
                <div>• <strong>模板范围（本页）</strong>：只能在“允许范围”内选择其一作为最终生效范围，超出将校验失败。</div>
                <div>• <strong>含义</strong>：global=不过滤；assigned=按已分配数据过滤；owned=按归属字段过滤；none=不开放。</div>
              </div>
            </div>
          }
        />
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              新增角色模板
            </Button>
            <Tooltip
              placement="bottom"
              title={
                <div>
                  <div>仅对你勾选的模板执行应用操作。</div>
                  <div>覆盖这些模板对应的角色矩阵，其他角色不受影响。</div>
                </div>
              }
            >
              <Button 
                onClick={() => {
                  const activeTemplateIds = templates.filter(t => t.is_active).map(t => t.id);
                  handleBatchApplyToMatrix(activeTemplateIds);
                }}
              >
                批量应用到权限矩阵
              </Button>
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={
                <div>
                  <div>对所有“已激活”的模板逐一应用。</div>
                  <div>用于全量对齐，覆盖这些角色的矩阵配置。</div>
                </div>
              }
            >
              <Button 
                onClick={handleSyncAllToMatrix}
                type="default"
              >
                同步所有模板到权限矩阵
              </Button>
            </Tooltip>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={templates}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      <Modal
        title={editingTemplate ? '编辑角色模板' : '新增角色模板'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="模板名称"
            rules={[{ required: true, message: '请输入模板名称' }]}
          >
            <Input placeholder="请输入模板名称" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea 
              rows={3} 
              placeholder="请输入模板描述"
            />
          </Form.Item>

          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select 
              placeholder="请选择角色"
              onChange={(value) => {
                setSelectedRole(value);
                form.setFieldsValue({ sub_role: undefined }); // 清空子角色选择
              }}
            >
              {ROLE_CHOICES.map(role => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedRole === 'customer' && (
            <Form.Item
              name="sub_role"
              label="子角色"
              rules={[]}
            >
              <Select placeholder="请选择子角色">
                {getSubRoleChoices(selectedRole).map(subRole => (
                  <Option key={subRole.value} value={subRole.value}>
                    {subRole.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="is_active"
            label="是否激活"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Card size="small" style={{ marginTop: 8 }}>
            <Space style={{ marginBottom: 8, flexWrap: 'wrap' }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder="按资源名称筛选"
                style={{ minWidth: 220 }}
                value={permResourceFilter}
                onChange={(val) => { setPermResourceFilter(val); setPermPagination(p => ({ ...p, current: 1 })); }}
              >
                {(resList || []).map(res => (
                  <Option key={res.key} value={res.key}>{res.name || res.key}</Option>
                ))}
              </Select>
              <Button onClick={() => setPermRows((rows) => rows.map((r) => ({ ...r, is_allowed: true })))}>全选</Button>
              <Button onClick={() => setPermRows((rows) => rows.map((r) => ({ ...r, is_allowed: false })))}>清空</Button>
            </Space>
            <Table
              size="small"
              rowKey={(r) => `${r.resource_key}.${r.action_key}`}
              pagination={{
                current: permPagination.current,
                pageSize: permPagination.pageSize,
                total: filteredPermRows.length,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10','20','50','100'],
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                onChange: (page, pageSize) => setPermPagination({ current: page, pageSize: pageSize || permPagination.pageSize }),
                onShowSizeChange: (_, size) => setPermPagination({ current: 1, pageSize: size })
              }}
              dataSource={filteredPermRows}
              columns={[
                { title: '资源', dataIndex: 'resource_key', render: (_: any, rec: TemplatePermissionRow) => resourceKeyToName[rec.resource_key] || rec.resource_key },
                { title: '动作', dataIndex: 'action_key' },
                {
                  title: '范围',
                  render: (_: any, rec: TemplatePermissionRow) => (
                    <Select
                      size="small"
                      value={rec.scope}
                      style={{ width: 140 }}
                      onChange={(val) => setPermRows((rows) => rows.map((r) => (r.resource_key === rec.resource_key && r.action_key === rec.action_key ? { ...r, scope: val } : r)))}
                    >
                      {(rec.allowed_scopes || []).map((s) => (
                        <Option key={s} value={s}>{s}</Option>
                      ))}
                    </Select>
                  ),
                },
                {
                  title: '允许',
                  render: (_: any, rec: TemplatePermissionRow) => (
                    <Switch
                      size="small"
                      checked={rec.is_allowed}
                      onChange={(checked) => setPermRows((rows) => rows.map((r) => (r.resource_key === rec.resource_key && r.action_key === rec.action_key ? { ...r, is_allowed: checked } : r)))}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleManagement;
